import { Rational } from '../money/rational.js';
export class InvalidRateStructureError extends Error {
    constructor(message) {
        super(message);
        this.name = 'InvalidRateStructureError';
    }
}
/** Refuses a structure that could not be evaluated, or that would charge less for longer. */
export function validateRateStructure(def) {
    const fail = (message) => {
        throw new InvalidRateStructureError(message);
    };
    if (def.minimum.isNegative())
        fail('The minimum cannot be negative.');
    if (def.kind === 'fixed') {
        if (def.unit !== null)
            fail('A fixed charge has no unit.');
        if (def.breakpoints.length > 0)
            fail('A fixed charge has no breakpoints.');
        if (def.extrapolation !== null)
            fail('A fixed charge has no rule beyond the table.');
        return;
    }
    if (def.unit === null)
        fail('A duration or quantity structure needs a unit.');
    if (def.increment.compareTo(Rational.ZERO) <= 0)
        fail('The increment must be positive.');
    if (def.extrapolation === null) {
        fail('A structure needs an explicit rule for quantities beyond its last breakpoint (PRC-01).');
    }
    let previous;
    for (const bp of def.breakpoints) {
        if (!Number.isInteger(bp.upTo) || bp.upTo <= 0) {
            fail('Each breakpoint covers up to a positive whole number of units.');
        }
        if (bp.charged.isNegative())
            fail('A breakpoint cannot charge a negative number of units.');
        if (previous && bp.upTo <= previous.upTo)
            fail('Breakpoints must be in increasing order.');
        if (previous && bp.charged.compareTo(previous.charged) < 0) {
            fail('A longer hire cannot charge fewer units than a shorter one.');
        }
        previous = bp;
    }
    const rule = def.extrapolation;
    if (rule.kind === 'repeat' && def.breakpoints.length === 0) {
        fail('Repeating the table needs a table to repeat.');
    }
    if (rule.kind === 'linear' && rule.perUnit.isNegative())
        fail('The rate beyond the table cannot be negative.');
    if (rule.kind === 'per_started_block') {
        if (!Number.isInteger(rule.blockUnits) || rule.blockUnits <= 0) {
            fail('A block is a positive whole number of units.');
        }
        if (rule.charged.isNegative())
            fail('A block cannot charge a negative number of units.');
    }
}
/**
 * Chargeable units for a measured quantity: days from the chargeable-day count (PRC-13), hours from
 * the sold window (OPS-04), kilometres from a trip. PRC-12 order, as far as this function reaches:
 * measure to the increment (PRC-11) → the curve (PRC-01) → the minimum (PRC-10).
 */
export function chargeableUnits(def, quantity) {
    validateRateStructure(def);
    if (def.kind === 'fixed')
        return maxOf(Rational.ONE, def.minimum);
    if (quantity.compareTo(Rational.ZERO) <= 0) {
        throw new RangeError('A structure prices a positive quantity; a line with none is not priced.');
    }
    const measured = toIncrement(quantity, def.increment, def.rounding);
    return maxOf(curve(def, measured), def.minimum);
}
function curve(def, q) {
    const table = def.breakpoints;
    for (const bp of table) {
        if (q.compareTo(Rational.fromInt(bp.upTo)) <= 0)
            return bp.charged;
    }
    const last = table.at(-1);
    const span = Rational.fromInt(last?.upTo ?? 0);
    const atSpan = last?.charged ?? Rational.ZERO;
    const beyond = q.subtract(span);
    const rule = def.extrapolation;
    switch (rule.kind) {
        case 'repeat': {
            // Whole spans before the last, partial one: 35 days on a 28-day table is one full span and 7
            // days; 56 is one full span and a second full one, never two spans and nothing.
            const fullSpans = ceil(q.divide(span)) - 1n;
            const rest = q.subtract(span.multiply(Rational.fromInt(fullSpans)));
            return atSpan.multiply(Rational.fromInt(fullSpans)).add(curve(def, rest));
        }
        case 'linear':
            return atSpan.add(beyond.multiply(rule.perUnit));
        case 'per_started_block': {
            const blocks = ceil(beyond.divide(Rational.fromInt(rule.blockUnits)));
            return atSpan.add(rule.charged.multiply(Rational.fromInt(blocks)));
        }
    }
}
/** PRC-11: bring a measured quantity to a whole number of increments. */
function toIncrement(q, increment, rounding) {
    if (rounding === 'exact')
        return q;
    const steps = q.divide(increment);
    const whole = rounding === 'up' ? ceil(steps) : floor(steps.add(Rational.of(1n, 2n))); // nearest: half up
    return increment.multiply(Rational.fromInt(whole));
}
function floor(x) {
    return x.floorDivMod().floor;
}
function ceil(x) {
    return -floor(x.negate());
}
function maxOf(a, b) {
    return a.compareTo(b) >= 0 ? a : b;
}
//# sourceMappingURL=rate-structure.js.map